import { useRef } from 'react';

import {
  DatePicker,
  DateRangePicker,
  Input,
  Select,
  Text,
  type TypeDatePickerRef,
  type TypeSelectOption,
} from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { SearchFilter } from '~/components/common';

import { getDateByOffset, validateNumberOnly } from '~/utils';

import { KEYWORD_PLACE_HOLDER, PERIOD_TYPE } from '~/constants/order';

import type {
  TypeRegisterFirstStepSearchFilterOptions,
  TypeRegisterFirstStepSearchFilterValues,
  TypeRegisterFirstStepSearchFilterValuesState,
} from '~/types/claim.types';

import styles from './ClaimRegisterFirstStepFilter.module.scss';

const ClaimRegisterFirstStepFilter = ({
  options,
  values,
  valuesStatus,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options: TypeRegisterFirstStepSearchFilterOptions;
  values: TypeRegisterFirstStepSearchFilterValues;
  valuesStatus: TypeRegisterFirstStepSearchFilterValuesState;
  onChange: (values: Partial<TypeRegisterFirstStepSearchFilterValues>) => void;
  onChangeStatus: (status: Partial<TypeRegisterFirstStepSearchFilterValuesState>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  const paymentEndDateRef = useRef<TypeDatePickerRef>(null);

  const handleChangePaymentTime = (
    range: 'start' | 'end',
    field: 'time' | 'minute',
    rawValue: string,
    options?: { pad?: boolean; numberOnly?: boolean }
  ) => {
    if (options?.numberOnly && !validateNumberOnly(rawValue)) return;

    const value = options?.pad ? rawValue.padStart(2, '0') : rawValue;

    const currentDate =
      (range === 'start' ? values.paymentStartDateTime?.date : values.paymentEndDateTime?.date) ?? null;
    const currentTime =
      (range === 'start' ? values.paymentStartDateTime?.time : values.paymentEndDateTime?.time) ?? null;
    const currentMinute =
      (range === 'start' ? values.paymentStartDateTime?.minute : values.paymentEndDateTime?.minute) ?? null;

    const updatedTime = field === 'time' ? value : currentTime;
    const updatedMinute = field === 'minute' ? value : currentMinute;

    onChange({
      [range === 'start' ? 'paymentStartDateTime' : 'paymentEndDateTime']: {
        date: currentDate,
        time: updatedTime,
        minute: updatedMinute,
      },
    });
  };

  const handleFocusPaymentDateTime = () => {
    onChangeStatus({ paymentDateTime: 'default' });
  };

  return (
    <SearchFilter formId="claim-return-register-first-step-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text className={styles['label']} size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.keyword}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
              onChangeStatus({ keyword: 'default' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder={KEYWORD_PLACE_HOLDER[values.keywordType?.value as keyof typeof KEYWORD_PLACE_HOLDER] || '입력'}
            value={values.keyword}
            status={valuesStatus.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
            onFocus={() => onChangeStatus({ keyword: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text className={styles['label']} size="small" weight="semi-bold" element="p">
          기간
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            maxWidth="132px"
            placeholder="선택"
            options={options.dateType}
            value={values.dateType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.dateType?.value) return;

              if (value?.value === PERIOD_TYPE.DELIVERY_DATE) onChangeStatus({ paymentDateTime: 'default' });
              if (value?.value === PERIOD_TYPE.PAYMENT_AT) onChangeStatus({ deliveryDate: 'default' });

              onChange({
                dateType: value,
                deliveryDate: { start: getDateByOffset(1, 'day'), end: getDateByOffset(1, 'day') },
                paymentStartDateTime: null,
                paymentEndDateTime: null,
              });
            }}
          />
          {values.dateType?.value === PERIOD_TYPE.DELIVERY_DATE && (
            <DateRangePicker
              size="small"
              maxWidth="354px"
              portalTarget={document.body}
              shouldCloseOnNotInView
              value={values.deliveryDate}
              status={valuesStatus.deliveryDate}
              onChange={(value) => {
                if (value === values.deliveryDate) return;
                onChange({ deliveryDate: value as { start: Date; end: Date } | null });
              }}
              onFocus={() => onChangeStatus({ deliveryDate: 'default' })}
            />
          )}
          {values.dateType?.value === PERIOD_TYPE.PAYMENT_AT && (
            <>
              <DatePicker
                size="small"
                maxWidth="140px"
                placeholder="날짜 선택"
                value={values.paymentStartDateTime?.date ?? null}
                status={valuesStatus.paymentDateTime}
                onChange={(value) => {
                  if (value === values.paymentStartDateTime?.date) return;

                  if (value === null) {
                    onChange({ paymentStartDateTime: null });
                    return;
                  }

                  onChange({
                    paymentStartDateTime: { date: value, time: '00', minute: '00' },
                  });
                  paymentEndDateRef.current?.focus();
                }}
                onFocus={handleFocusPaymentDateTime}
              />
              <Input
                type="text"
                size="small"
                maxWidth="40px"
                maxLength={2}
                placeholder="시"
                value={values.paymentStartDateTime?.time ?? ''}
                status={valuesStatus.paymentDateTime}
                onChange={(e) => handleChangePaymentTime('start', 'time', e.target.value, { numberOnly: true })}
                onBlur={(e) => handleChangePaymentTime('start', 'time', e.target.value, { pad: true })}
                onFocus={handleFocusPaymentDateTime}
              />
              <Text size="large">:</Text>
              <Input
                type="text"
                size="small"
                maxWidth="40px"
                maxLength={2}
                placeholder="분"
                value={values.paymentStartDateTime?.minute ?? ''}
                status={valuesStatus.paymentDateTime}
                onChange={(e) => handleChangePaymentTime('start', 'minute', e.target.value, { numberOnly: true })}
                onBlur={(e) => handleChangePaymentTime('start', 'minute', e.target.value, { pad: true })}
                onFocus={handleFocusPaymentDateTime}
              />
              <Text size="large">-</Text>
              <DatePicker
                ref={paymentEndDateRef}
                size="small"
                maxWidth="140px"
                placeholder="날짜 선택"
                value={values.paymentEndDateTime?.date ?? null}
                status={valuesStatus.paymentDateTime}
                onChange={(value) => {
                  if (value === values.paymentEndDateTime?.date) return;

                  if (value === null) {
                    onChange({ paymentEndDateTime: null });
                    return;
                  }

                  onChange({
                    paymentEndDateTime: { date: value, time: '23', minute: '59' },
                  });
                }}
                onFocus={handleFocusPaymentDateTime}
              />
              <Input
                type="text"
                size="small"
                maxWidth="40px"
                maxLength={2}
                placeholder="시"
                value={values.paymentEndDateTime?.time ?? ''}
                status={valuesStatus.paymentDateTime}
                onChange={(e) => handleChangePaymentTime('end', 'time', e.target.value, { numberOnly: true })}
                onBlur={(e) => handleChangePaymentTime('end', 'time', e.target.value, { pad: true })}
                onFocus={handleFocusPaymentDateTime}
              />
              :
              <Input
                type="text"
                size="small"
                maxWidth="40px"
                maxLength={2}
                placeholder="분"
                value={values.paymentEndDateTime?.minute ?? ''}
                status={valuesStatus.paymentDateTime}
                onChange={(e) => handleChangePaymentTime('end', 'minute', e.target.value, { numberOnly: true })}
                onBlur={(e) => handleChangePaymentTime('end', 'minute', e.target.value, { pad: true })}
                onFocus={handleFocusPaymentDateTime}
              />
            </>
          )}
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-type'])}>
        <Text className={styles['label']} size="small" weight="semi-bold" element="p">
          배송유형
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.deliveryType}
            value={values.deliveryType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.deliveryType?.value) return;
              onChange({ deliveryType: value });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-courier'])}>
        <Text className={styles['label']} size="small" weight="semi-bold" element="p">
          배송사
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.courier}
            value={values.courier}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.courier?.value) return;
              onChange({ courier: value });
            }}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-cluster-center'])}>
        <Text className={styles['label']} size="small" weight="semi-bold" element="p">
          생산센터
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.clusterCenter}
            value={values.clusterCenter}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.clusterCenter?.value) return;
              onChange({ clusterCenter: value });
            }}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default ClaimRegisterFirstStepFilter;
